import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  Flex,
  Portal,
  Spinner,
  Toast,
  Toaster,
  createToaster
} from "@chakra-ui/react";

import { Icon } from "../Icon";

import { useColorTokens } from "../../theme/hooks/useColorTokens";
import { FeedbackIcons } from "../../theme/icons";
import type { ReactNode } from "react";
import { Button } from "../Button";

type ColorTokens = ReturnType<typeof useColorTokens>;

export interface NotificationToastProps {
  type: "error" | "success" | "warning" | "info" | "loading";
  title?: string | ReactNode;
  description?: ReactNode;
  action?: {
    label: string;
    onClick: () => void;
    variant?:
      | "primary"
      | "secondary"
      | "tertiary"
      | "primaryHollow"
      | "secondaryHollow"
      | "tertiaryHollow";
  };
  meta?: {
    closable?: boolean;
  };
}

const getIcon = (type: string, colors: ColorTokens) => {
  const { success, danger, warning, action } = colors;
  switch (type) {
    case "success":
      return (
        <Icon
          icon={<FeedbackIcons.Tick />}
          size={24}
          fillColor="success.800"
        />
      );
    case "error":
      return (
        <Icon
          icon={<FeedbackIcons.Error />}
          size={24}
          fillColor="danger.500"
        />
      );

    case "warning":
      return (
        <Icon
          icon={<FeedbackIcons.Warning />}
          size={24}
          fillColor="warning.500"
        />
      );

    case "info":
      return (
        <Icon icon={<FeedbackIcons.Info />} size={24} fillColor="action.600" />
      );

    default:
      return (
        <Icon icon={<FeedbackIcons.Info />} size={24} fillColor="action.600" />
      );
  }
};

const getBgColor = (type: string) => {
  switch (type) {
    case "success":
      return "success.300";
    case "error":
      return "danger.300";
    case "warning":
      return "warning.300";
    case "info":
      return "base.300";
    default:
      return "base.300";
  }
};

const getBorderColor = (type: string) => {
  switch (type) {
    case "success":
      return "success.500";
    case "error":
      return "danger.500";
    case "warning":
      return "warning.500";
    case "info":
      return "base.500";
    default:
      return "base.500";
  }
};

export const appToaster = createToaster({
  placement: "bottom",
  pauseOnPageIdle: false,
  duration: 5000,
  offsets: { left: "16px", top: "16px", right: "16px", bottom: "24px" }
});

const AppToast = () => {
  const colors = useColorTokens();
  const { base } = colors;

  return (
    <Portal>
      <Toaster toaster={appToaster} insetInline={{ base: "4", md: "6" }}>
        {toast => (
          <Toast.Root
            maxWidth={{ base: "calc(100vw - 32px)", md: "620px" }}
            borderRadius="12px"
            padding="16px"
            alignItems={{ base: "flex-start", md: "center" }}
            backgroundColor={getBgColor(toast.type as string)}
            borderWidth="1px"
            borderColor={getBorderColor(toast.type as string)}
            data-tracking-id={toast.description}
            height="auto"
            gap="12px"
            position="relative"
          >
            <Flex
              alignItems={{ base: "flex-start", md: "center" }}
              marginTop={{ base: "2px", md: "0" }}
            >
              {toast.type === "loading" ? (
                <Spinner size="sm" color="brand.500" />
              ) : (
                getIcon(toast.type as string, colors)
              )}
            </Flex>
            <Flex
              direction={{ base: "column", md: "row" }}
              flex="1"
              maxWidth="100%"
              alignItems={{ base: "flex-start", md: "center" }}
              gap={{ base: "2px", md: "8px" }}
            >
              <Flex
                direction="column"
                flex="1"
                alignItems="flex-start"
                minWidth="0"
                overflow="hidden"
                paddingRight={{
                  base: toast.meta?.closable ? "36px" : "0",
                  md: "0"
                }}
              >
                <Toast.Title
                  color="base.700"
                  as="span"
                  textStyle="body.large"
                  fontWeight="bold"
                  wordBreak="break-word"
                  overflowWrap="break-word"
                >
                  {toast.title && toast.title + "."}
                  {toast.description && (
                    <Toast.Description
                      textStyle="body.large"
                      color="base.700"
                      paddingLeft={1}
                      wordBreak="break-word"
                      overflowWrap="break-word"
                    >
                      {toast.description}
                    </Toast.Description>
                  )}
                </Toast.Title>
              </Flex>
              {toast.action && toast.action.label && (
                <Toast.ActionTrigger
                  alignSelf={{
                    base: "flex-start",
                    md: "center"
                  }}
                  marginTop={{ base: "8px", md: "0" }}
                  flexShrink={0}
                  display="flex"
                  alignItems="center"
                  justifyContent={{
                    base: "flex-start",
                    md: "center"
                  }}
                  marginLeft={{ base: "0", md: "4px" }}
                  width="auto"
                  minWidth="fit-content"
                  padding="0"
                  border="none"
                  outline="none"
                  background="transparent"
                >
                  <Button
                    variant={
                      (toast.action as NotificationToastProps["action"])
                        ?.variant || "secondary"
                    }
                    size="sm"
                    bg="action.600"
                    color="base.200"
                    borderColor="action.600"
                  >
                    {toast.action.label}
                  </Button>
                </Toast.ActionTrigger>
              )}
            </Flex>
            {toast.meta?.closable && (
              <Toast.CloseTrigger
                alignSelf={{ base: "flex-start", md: "center" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                padding="4px"
                marginTop="0"
                cursor="pointer"
                marginLeft={{ base: "auto", md: "4px" }}
                borderRadius="4px"
                _hover={{ backgroundColor: "base.100" }}
                transition="background-color 0.2s"
                flexShrink={0}
                position={{ base: "absolute", md: "static" }}
                top={{ base: "12px", md: "auto" }}
                right={{ base: "12px", md: "auto" }}
              >
                <FontAwesomeIcon icon={faXmark} color={base[700]} size="lg" />
              </Toast.CloseTrigger>
            )}
          </Toast.Root>
        )}
      </Toaster>
    </Portal>
  );
};

export default AppToast;
